import { useGetBankingAuthExpiration } from "Api/Queries/Banks/useGetBankingAuthExpiration";
import type { BankAccountProblemAlertProps } from "Components/BankAccounts/BankAccountDetail/BankAccountProblemAlert";
import { useBankingAuth } from "Hooks/useBankingAuth";
import { useBankingPispAuth } from "Hooks/useBankingPispAuth";
import {
  type BankAccountListItem,
  BankAccountStatus,
} from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { toArray } from "Utils/ArrayUtils";
import { formatAmount } from "Utils/CurrencyUtils";
import { appUrl } from "Utils/UrlUtils";

const ALERTS_RESOURCES = Resources.Pages.Content.BankAccounts.Detail.Alerts;

export const useBankAccountProblemAlert = ({
  status,
  environment,
  isTokenInvalid,
  isReceiveOnly,
  paymentLimits,
}: BankAccountListItem): BankAccountProblemAlertProps | null => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { data: expirationData } = useGetBankingAuthExpiration();
  const {
    authorize,
    isPending: isAisAuthPending,
    selectedEnvironment: aisEnvironment,
  } = useBankingAuth();
  const {
    authorize: authorizePisp,
    isPending: isPispAuthPending,
    selectedEnvironment: pispEnvironment,
  } = useBankingPispAuth();

  const invalidBanks = toArray(expirationData?.invalidBanks);
  const expiringBanks = toArray(expirationData?.expiringBanks);
  const invalidPispBanks = toArray(expirationData?.invalidPispBanks);
  const expiringPispBanks = toArray(expirationData?.expiringPispBanks);

  const isAisInvalid = isTokenInvalid || invalidBanks.includes(environment);
  const isAisExpiring = !isAisInvalid && expiringBanks.includes(environment);
  const isPispInvalid =
    !isReceiveOnly && invalidPispBanks.includes(environment);
  const isPispExpiring =
    !isReceiveOnly && !isPispInvalid && expiringPispBanks.includes(environment);

  const isAisActionLoading = isAisAuthPending && aisEnvironment === environment;
  const isPispActionLoading =
    isPispAuthPending && pispEnvironment === environment;

  if (status === BankAccountStatus.Rejected) {
    return {
      severity: "error",
      title: t(ALERTS_RESOURCES.Rejected.Title),
      description: t(ALERTS_RESOURCES.Rejected.Description),
    };
  }

  if (status === BankAccountStatus.Inactive) {
    return {
      severity: "info",
      title: t(ALERTS_RESOURCES.Inactive.Title),
      description: t(ALERTS_RESOURCES.Inactive.Description),
    };
  }

  if (status === BankAccountStatus.Restricted) {
    return {
      severity: "warning",
      title: t(ALERTS_RESOURCES.Restricted.Title),
      description: t(ALERTS_RESOURCES.Restricted.Description),
      actionLabel: t(ALERTS_RESOURCES.Restricted.Action),
      onAction: () => navigate(appUrl("complete-customer-onboarding")),
    };
  }

  if (status === BankAccountStatus.AwaitingVerification) {
    return {
      severity: "warning",
      title: t(ALERTS_RESOURCES.AwaitingVerification.Title),
      description: t(ALERTS_RESOURCES.AwaitingVerification.Description),
    };
  }

  if (isAisInvalid) {
    return {
      severity: "error",
      title: t(ALERTS_RESOURCES.TokenInvalidAis.Title),
      description: t(ALERTS_RESOURCES.TokenInvalidAis.Description),
      actionLabel: t(ALERTS_RESOURCES.TokenInvalidAis.Action),
      onAction: () => authorize({ environment }),
      isActionLoading: isAisActionLoading,
    };
  }

  if (isPispInvalid) {
    return {
      severity: "error",
      title: t(ALERTS_RESOURCES.TokenInvalidPisp.Title),
      description: t(ALERTS_RESOURCES.TokenInvalidPisp.Description),
      actionLabel: t(ALERTS_RESOURCES.TokenInvalidPisp.Action),
      onAction: () => authorizePisp({ environment }),
      isActionLoading: isPispActionLoading,
    };
  }

  if (isAisExpiring) {
    return {
      severity: "warning",
      title: t(ALERTS_RESOURCES.TokenExpiringAis.Title),
      description: t(ALERTS_RESOURCES.TokenExpiringAis.Description),
      actionLabel: t(ALERTS_RESOURCES.TokenExpiringAis.Action),
      onAction: () => authorize({ environment }),
      isActionLoading: isAisActionLoading,
    };
  }

  if (isPispExpiring) {
    return {
      severity: "warning",
      title: t(ALERTS_RESOURCES.TokenExpiringPisp.Title),
      description: t(ALERTS_RESOURCES.TokenExpiringPisp.Description),
      actionLabel: t(ALERTS_RESOURCES.TokenExpiringPisp.Action),
      onAction: () => authorizePisp({ environment }),
      isActionLoading: isPispActionLoading,
    };
  }

  // hasLimit is false for a stale PreApproved row when the merchant pool already has a verified
  // (Active) account — the caps no longer apply, so don't render an alert with zero limits.
  if (status === BankAccountStatus.PreApproved && paymentLimits?.hasLimit) {
    return {
      severity: "info",
      title: t(ALERTS_RESOURCES.PreApproved.Title),
      description: t(ALERTS_RESOURCES.PreApproved.Description, {
        remaining: formatAmount(paymentLimits.preApprovalRemainingAmount),
        limit: formatAmount(paymentLimits.preApprovalTotalLimit),
      }),
    };
  }

  return null;
};
